import React, { useState, useEffect, useId, useCallback } from 'react';
import { XMarkIcon, PhotoIcon, FilmIcon } from '@heroicons/react/24/outline';
import { advertisementService } from '../services/advertisementService';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';

// Schéma de validation corrigé avec Zod
const advertisementSchema = z.object({
  title: z.string().min(3, "Le titre doit contenir au moins 3 caractères"),
  type: z.enum(['image', 'video']),
  description: z.string().optional(),
  banner: z
    .any()
    .refine(file => file instanceof File || typeof file === 'string' || file instanceof Blob, "Le fichier est requis")
    .refine(file => {
      if (file instanceof File) {
        return file.size <= 5 * 1024 * 1024;
      }
      return true; // Ne pas vérifier la taille si c'est une string (URL) ou autre
    }, "Le fichier ne doit pas dépasser 5MB"),
  url: z.string().url("L'URL doit être valide").nullable().or(z.literal('')),
  start_date: z.string().min(1, "La date de début est requise"),
  end_date: z.string().min(1, "La date de fin est requise")
    .refine((end, context) => {
      const start = context?.parent?.start_date;
      return !start || new Date(end) >= new Date(start);
    }, "La date de fin doit être égale ou postérieure à la date de début"),
  is_active: z.boolean()
});

const AdvertisementModal = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  advertisement 
}) => {
  const [preview, setPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // id unique pour éviter les collisions d'ID dans le DOM
  const isActiveId = useId();

  const { 
    register, 
    handleSubmit, 
    watch, 
    setValue, 
    reset, 
    control,
    formState: { errors } 
  } = useForm({
    resolver: zodResolver(advertisementSchema),
    defaultValues: {
      title: '',
      type: 'image',
      description: '',
      url: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +7 jours par défaut
      is_active: true
    },
    mode: 'onChange' // Valider à chaque changement
  });

  const fileType = watch('type');

  // Valider le type de fichier séparément
  const validateFileType = useCallback((file) => {
    if (!file || typeof file === 'string') return true;
    
    const currentFileType = watch('type');
    if (currentFileType === 'image') {
      return ['image/jpeg', 'image/png'].includes(file.type);
    } else {
      return ['video/mp4', 'video/quicktime', 'video/x-msvideo'].includes(file.type);
    }
  }, [watch]);

  // Initialiser le formulaire
  useEffect(() => {
    if (advertisement) {
      reset({
        title: advertisement.title,
        type: advertisement.type,
        description: advertisement.description || '',
        url: advertisement.url || '',
        start_date: advertisement.start_date.split('T')[0],
        end_date: advertisement.end_date.split('T')[0],
        is_active: advertisement.is_active,
        banner: advertisement.banner
      });
      setPreview(advertisement.banner_url);
    } else {
      reset({
        title: '',
        type: 'image',
        description: '',
        url: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        is_active: true,
        banner: null
      });
      setPreview(null);
    }
    setErrorMessage('');
  }, [advertisement, isOpen, reset]);

  // Surveiller les changements de type pour valider à nouveau le fichier
  useEffect(() => {
    const bannerValue = watch('banner');
    if (bannerValue && bannerValue instanceof File) {
      const isValid = validateFileType(bannerValue);
      if (!isValid) {
        setValue('banner', null, { shouldValidate: true });
        setPreview(null);
        setErrorMessage(`Format de fichier non supporté pour les ${fileType}s`);
      }
    }
  }, [fileType, watch, setValue, validateFileType]);

  // Gestion du fichier média
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!validateFileType(file)) {
      setErrorMessage(`Format de fichier non supporté pour les ${fileType}s`);
      return;
    }
    
    // Vérifier la taille du fichier
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage("Le fichier ne doit pas dépasser 5MB");
      return;
    }

    setErrorMessage('');
    setValue('banner', file, { shouldValidate: true, shouldDirty: true });

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(URL.createObjectURL(file));
    }
  };

  const onSubmit = async (data) => {
    // Vérification finale des données avant envoi
    if (!data.banner && !advertisement) {
      setErrorMessage("Veuillez sélectionner un fichier");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    try {
      if (advertisement) {
        // Si le banner n'a pas changé, utilisez l'original
        const formData = {...data};
        if (formData.banner === advertisement.banner) {
          // Pas besoin d'envoyer à nouveau le fichier
          delete formData.banner;
        }
        await advertisementService.updateAdvertisement(advertisement.id, formData);
      } else {
        await advertisementService.createAdvertisement(data);
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement", error);
      setErrorMessage(error.message || "Une erreur est survenue lors de l'enregistrement");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-800">
            {advertisement ? 'Modifier la publicité' : 'Nouvelle publicité'}
          </h2>
          <button 
            onClick={onClose} 
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Fermer"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {errorMessage}
            </div>
          )}

          {/* Titre */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Titre <span className="text-red-500">*</span>
            </label>
            <input
              id="title"
              {...register('title')}
              type="text"
              placeholder="Entrez le titre de la publicité"
              className={`w-full px-3 py-2 border rounded-md shadow-sm ${
                errors.title ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-300'
              } focus:outline-none focus:ring-primary focus:border-primary transition-colors`}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
            )}
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de média <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className={`flex items-center justify-center p-4 border rounded-md cursor-pointer transition-all ${
                fileType === 'image' 
                  ? 'bg-primary/10 border-primary shadow-sm' 
                  : 'border-gray-300 hover:bg-gray-50'
              }`}>
                <input
                  type="radio"
                  {...register('type')}
                  value="image"
                  className="sr-only"
                />
                <PhotoIcon className={`h-6 w-6 mr-2 ${fileType === 'image' ? 'text-primary' : 'text-gray-400'}`} />
                <span className={fileType === 'image' ? 'font-medium text-primary' : 'text-gray-700'}>Image</span>
              </label>
              
              <label className={`flex items-center justify-center p-4 border rounded-md cursor-pointer transition-all ${
                fileType === 'video' 
                  ? 'bg-primary/10 border-primary shadow-sm' 
                  : 'border-gray-300 hover:bg-gray-50'
              }`}>
                <input
                  type="radio"
                  {...register('type')}
                  value="video"
                  className="sr-only"
                />
                <FilmIcon className={`h-6 w-6 mr-2 ${fileType === 'video' ? 'text-primary' : 'text-gray-400'}`} />
                <span className={fileType === 'video' ? 'font-medium text-primary' : 'text-gray-700'}>Vidéo</span>
              </label>
            </div>
          </div>

          {/* Importation du média */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {fileType === 'image' ? 'Image' : 'Vidéo'} <span className="text-red-500">*</span>
            </label>
            
            <div className={`mt-1 flex justify-center rounded-lg border border-dashed p-6 ${
              errors.banner 
                ? 'border-red-300 bg-red-50' 
                : preview 
                  ? 'border-primary/30 bg-primary/5'
                  : 'border-gray-300 hover:border-gray-400'
            } transition-colors`}>
              <div className="text-center">
                {preview ? (
                  fileType === 'image' ? (
                    <div className="relative">
                      <img
                        src={preview}
                        alt="Aperçu"
                        className="mx-auto max-h-48 rounded-md object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreview(null);
                          setValue('banner', null, { shouldValidate: true });
                        }}
                        className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md hover:bg-red-50 transition-colors"
                        aria-label="Supprimer l'image"
                      >
                        <XMarkIcon className="h-5 w-5 text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="bg-gray-100 rounded-md p-4 flex items-center justify-center">
                        <video 
                          src={preview}
                          className="max-h-48 rounded-md" 
                          controls
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-600">
                        {watch('banner')?.name || 'Fichier vidéo sélectionné'}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setPreview(null);
                          setValue('banner', null, { shouldValidate: true });
                        }}
                        className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md hover:bg-red-50 transition-colors"
                        aria-label="Supprimer la vidéo"
                      >
                        <XMarkIcon className="h-5 w-5 text-red-500" />
                      </button>
                    </div>
                  )
                ) : (
                  <>
                    {fileType === 'image' ? (
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                    ) : (
                      <FilmIcon className="mx-auto h-12 w-12 text-gray-400" />
                    )}
                    <div className="mt-4 flex flex-col text-sm leading-6 text-gray-600">
                      <label
                        htmlFor="banner-upload"
                        className="relative cursor-pointer rounded-md bg-white font-medium text-primary hover:text-primary-dark focus-within:outline-none inline-block mx-auto mb-2 px-4 py-2 border border-primary/30 hover:bg-primary/5 transition-colors"
                      >
                        <span>Téléverser un fichier</span>
                        <input
                          id="banner-upload"
                          type="file"
                          accept={fileType === 'image' ? 'image/jpeg,image/png' : 'video/mp4,video/quicktime,video/x-msvideo'}
                          onChange={handleFileChange}
                          className="sr-only"
                        />
                      </label>
                      <p className="text-center">ou glisser-déposer</p>
                    </div>
                    <p className="text-xs leading-5 text-gray-600 mt-2">
                      {fileType === 'image' 
                        ? 'PNG, JPG (max. 5MB)' 
                        : 'MP4, MOV, AVI (max. 5MB)'}
                    </p>
                  </>
                )}
              </div>
            </div>
            {errors.banner && (
              <p className="mt-1 text-sm text-red-600">{errors.banner.message}</p>
            )}
          </div>

          {/* URL */}
          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
              Lien de destination (optionnel)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                id="url"
                {...register('url')}
                type="url"
                placeholder="https://example.com"
                className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm ${
                  errors.url ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-300'
                } focus:outline-none focus:ring-primary focus:border-primary transition-colors`}
              />
            </div>
            {errors.url && (
              <p className="mt-1 text-sm text-red-600">{errors.url.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (optionnel)
            </label>
            <textarea
              id="description"
              {...register('description')}
              rows={3}
              placeholder="Décrivez votre publicité..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary transition-colors"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-1">
                Date de début <span className="text-red-500">*</span>
              </label>
              <input
                id="start_date"
                {...register('start_date')}
                type="date"
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 border rounded-md shadow-sm ${
                  errors.start_date ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-300'
                } focus:outline-none focus:ring-primary focus:border-primary transition-colors`}
              />
              {errors.start_date && (
                <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin <span className="text-red-500">*</span>
              </label>
              <input
                id="end_date"
                {...register('end_date')}
                type="date"
                min={watch('start_date') || new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 border rounded-md shadow-sm ${
                  errors.end_date ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-300'
                } focus:outline-none focus:ring-primary focus:border-primary transition-colors`}
              />
              {errors.end_date && (
                <p className="mt-1 text-sm text-red-600">{errors.end_date.message}</p>
              )}
            </div>
          </div>

          {/* Statut (visible seulement en édition) */}
          <div className="flex items-center">
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <div className="flex items-center">
                  {/* wrapper cliquable/accessible pour le switch */}
                  <div
                    className="relative inline-block w-10 mr-2 align-middle select-none cursor-pointer"
                    role="switch"
                    tabIndex={0}
                    aria-checked={!!field.value}
                    onClick={() => field.onChange(!field.value)}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        field.onChange(!field.value);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      id={isActiveId}
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`block h-6 rounded-full w-10 ${field.value ? 'bg-primary' : 'bg-gray-300'} transition-colors`}></div>
                    <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform ${field.value ? 'translate-x-4' : ''}`}></div>
                  </div>
                  <label htmlFor={isActiveId} className="text-sm text-gray-700 cursor-pointer">
                    Publicité active
                  </label>
                </div>
              )}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enregistrement...
                </span>
              ) : (
                <span>{advertisement ? 'Mettre à jour' : 'Créer la publicité'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdvertisementModal;